<template>
  <div
    v-if="pageState"
    class="list-container"
  >
    <div class="filter-column">
      <ui-search-form
        dateMode="range"
        :searchTermPlaceholder="'Search reservations on name, id'"
        :isLoading="isSearching"
        :useDateInput="pageState.useDateInput"
        :startDate="pageState.startDate"
        :endDate="pageState.endDate"
        :secondStatusId="pageState.channelId"
        :secondStatusOptions="locations"
        :actionSearch="searchReservations"
      />
    </div>

    <div
      v-if="pageState && pageState.statusses && pageState.statusses.length > 0 && pageState.selectedStatus"
      class="tabs is-boxed has-margin-top"
    >
      <ul>
        <li
          v-for="(status, index) in pageState.statusses"
          :key="index"
          :class="{ 'is-active' : status.status === pageState.selectedStatus.status, 'is-loading': isSearching }"
        >
          <a @click="setSelectedStatus(status)">{{ $t('ReservationStatus.status_' + status.status) }} ({{ status.count }})</a>
        </li>
      </ul>
    </div>

    <div class="list-wrapper">
      <div class="">
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th width="125px">#</th>
              <th width="300px">Location</th>
              <th>Name </th>
              <th>Tags </th>
              <th width="150px">Created on</th>
              <th width="100px">Date</th>
              <th width="75px">Start</th>
              <th width="75px">End</th>
              <th width="75px">Seats</th>
              <th width="75px">IB</th>
            </tr>
          </thead>
          <transition-group
            name="staggered-fade"
            v-bind:css="false"
            v-on:before-enter="beforeEnter"
            v-on:enter="enter"
            tag="tbody"
            v-if="pageState && 
            pageState.selectedStatus && 
            pageState.selectedStatus.response &&
            pageState.selectedStatus.response.Results && 
            pageState.selectedStatus.response.Results.length > 0"
          >
            <tr
              v-for="(reservation, index) in pageState.selectedStatus.response.Results"
              :key="'tr_' + index"
              :data-index="index"
            >
              <td>
                <!-- <a @click="setShowReservationSummary(reservation.Key)">#{{ reservation.Id }}</a><br /> -->
                #{{ reservation.Id }}
              </td>
              <td>{{ reservation.LocationName }}</td>
              <td>{{ reservation.Name }}</td>
              <td>
                <Tags
                  v-if="reservation.Tags.length > 0"
                  :tags="reservation.Tags.split(',')"
                  :tagCssClass="'is-link is-light'"
                />
              </td>
              <td>{{ reservation.CreatedOn | convertTimeStampToIsoDateTimeString }}</td>
              <td nowrap>
                {{
                  reservation.Date
                  | parseIsoDateStringToDate
                  | dateObjectIsoDateString
                }}
              </td>
              <td>{{ reservation.StartMinutes | minutesToTime }}</td>
              <td>{{ reservation.EndMinutes | minutesToTime }}</td>
              <td>{{ reservation.TotalSeats }}</td>
              <td>
                <span v-if="reservation.IsInstantBook" class="icon has-text-success">
                  <font-awesome-icon :icon="['fas', 'check']" />
                </span>
              </td>
            </tr>
          </transition-group>
        </table>

        <transition
          name="fade"
          mode="out-in"
        >
          <Message
            v-if="!isSearching &&
            pageState &&
            pageState.selectedStatus &&
            pageState.selectedStatus.response &&
            pageState.selectedStatus.response.Results && 
            pageState.selectedStatus.response.Results.length === 0"
            :message="$t('Components.Reservation.ReservationList.Message_NoReservations')"
          />
        </transition>

        <transition
          name="fade"
          mode="out-in"
        >
          <ui-loader v-if="isSearching" />
        </transition>

        <div v-if="!isSearching">
          <Pagination
            v-if="pageState.selectedStatus &&
            pageState.selectedStatus.response &&
            (pageState.selectedStatus.response.MorePages > 0 ||
              pageState.selectedStatus.page > 1)"
            :currentPage="pageState.selectedStatus.page ? pageState.selectedStatus.page : 1"
            :morePages="pageState.selectedStatus.response.MorePages"
            @pageSelected="pageSelected"
          />
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import staggeredAnimation from '@/mixins/staggeredAnimation'
import locationProvider from '@/providers/location'
import reservationProvider from '@/providers/reservation'
import { format } from 'date-fns'

import Message from '@/components/UI/Message'
import Pagination from '@/components/UI/Pagination'
import Tags from '@/components/UI/Tags'

export default {
  components: {
    Message,
    Pagination,
    Tags,
  },

  props: {
    selectedMeetingtype: {
      type: Number,
      default: 0,
    },

    showBooker: {
      type: Boolean,
      default: true,
    },

    showCompany: {
      type: Boolean,
      default: true,
    },
  },

  mixins: [staggeredAnimation],

  data() {
    return {
      isLoadingLocations: false,
      channelId: Number(this.$route.params.channelId),
      locationId: 0,
      itemId: 0,
      pageState: this.$store.getters['reservationStore/getReservationsPageState'],
      locations: [],
      isSearchingCounter: 0
    }
  },

  computed: {
    isSearching() {
      return this.isSearchingCounter > 0
    },
  },

  watch: {
    selectedMeetingtype: {
      handler: function(newVal) {
        this.setMeetingtype(newVal);
      },
    },
  },

  created() {
    if (this.companyId > 0 || this.profileId > 0) {
      let currentDate = new Date()

      this.pageState.startDate = new Date(
        currentDate.getFullYear(),
        0,
        1,
        currentDate.getHours(),
        currentDate.getMinutes()
      )
      this.pageState.endDate = new Date(
        currentDate.getFullYear(),
        11,
        31,
        currentDate.getHours(),
        currentDate.getMinutes()
      )
    }

    if (this.selectedMeetingtype) {
      this.pageState.meetingtypeId = this.selectedMeetingtype
    }

    this.getChannelReservationStatusCount();
    this.getLocations();
  },

  beforeDestroy() {
    if (this.companyId > 0 || this.profileId > 0) {
      this.resetReservationPageState()
    }

    reservationProvider.methods.cancelGetLocationReservationsRequest();
  },

  methods: {
    ...mapActions('reservationStore', ['resetReservationPageState']),
    ...mapMutations('reservationStore', [
      'setReservation',
      'setReservationsPageState',
    ]),

    getLocations() {
      if (this.isLoadingLocations) {
        return;
      }

      this.isLoadingLocations = true;

      let searchTerm = ''
      let page = 0
      let itemsPerPage = 0
      let countryId = 0

      locationProvider.methods
        .getLocations(
          this.channelId,
          searchTerm,
          page,
          itemsPerPage,
          -2,
          -1,
          countryId
        )
        .then((response) => {
          let data = response.data.Results;
          let locs = [];
          locs.push({ id: 0, text: 'All locations' });

          for (let i= 0; i < data.length; i++) {
            locs.push({ id: data[i].Id, text: `#${data[i].Id} - ${data[i].Name}` });
          }

          this.locations = locs;
        })
        .finally(() => {
          this.isLoadingLocations = false;
        })
    },

    getChannelReservationStatusCount() {
      this.isSearchingCounter++;

      reservationProvider.methods.getChannelReservationStatusCount(
        this.channelId,
        this.pageState.locationId,
        this.pageState.searchTerm,
        format(this.pageState.startDate, 'yyyy-MM-dd'),
        format(this.pageState.endDate, 'yyyy-MM-dd'),
        this.pageState.meetingtypeId,
        this.companyId,
        this.customerId,
        this.leadId,
        this.voucherId
      )
        .then((response) => {
          if (response.status === 200) {
            let data = response.data
            let statusses = [];

            for (let i = 0; i < data.length; i++) {
              statusses.push({ status: data[i].Status, count: data[i].NrOfReservations, response: [], page: 1 });
            }

            this.pageState.statusses = statusses;
            this.setReservationsPageState(this.pageState);
          }
        })
        .finally(() => {
          this.isSearchingCounter = Math.max(--this.isSearchingCounter, 0);

          if (this.pageState.selectedStatus === null) {
            this.setSelectedStatus(this.pageState.statusses[2]);
          } else {
            this.getChannelReservations();
          }
        })
    },

    /**
     *  Get channel reservations
     */
    async getChannelReservations() {
      this.isSearchingCounter++;

      // Clear store data 
      reservationProvider.methods.cancelGetLocationReservationsRequest()

      reservationProvider.methods.getChannelReservations(
        this.pageState.searchTerm,
        this.channelId,
        this.pageState.locationId,
        this.customerId,
        this.companyId,
        this.pageState.meetingtypeId,
        this.pageState.selectedStatus.status,
        format(this.pageState.startDate, 'yyyy-MM-dd'),
        format(this.pageState.endDate, 'yyyy-MM-dd'),
        this.pageState.selectedStatus.page,
        this.pageState.itemsPerPage,
        this.voucherId
      )
        .then((response) => {
          if (response.status === 200) {
            this.pageState.selectedStatus.response = response.data;
            this.setReservationsPageState(this.pageState);
          }
        })
        .catch(() => {
        })
        .finally(() => {
          this.isSearchingCounter = Math.max(--this.isSearchingCounter, 0);
        });
    },

    setMeetingtype(meetingtypeId) {
      this.pageState.selectedStatus.page = 1;
      this.pageState.selectedStatus.response.Results = [];

      this.pageState.meetingtypeId = meetingtypeId;
      this.setReservationsPageState(this.pageState);

      this.getChannelReservationStatusCount();
    },

    setSelectedStatus(status) {
      let statusIndex = this.pageState.statusses.findIndex((s) => s.status === status.status);
      if (statusIndex > -1) {
        this.pageState.selectedStatus = this.pageState.statusses[statusIndex];
        this.pageState.selectedStatus.page = 1;
        this.pageState.selectedStatus.response.Results = [];

        this.setReservationsPageState(this.pageState);

        // get reservations
        this.getChannelReservations();
      }
    },

    searchReservations(searchQuery) {
      this.pageState.selectedStatus.page = 1;
      this.pageState.selectedStatus.response.Results = [];

      let now = new Date();
      let halfYearPast = new Date(now);
      halfYearPast.setMonth(halfYearPast.getMonth() - 6);

      let datePast = new Date(now);
      datePast.setFullYear(datePast.getFullYear() - 1);

      let dateFuture = new Date(now);
      dateFuture.setFullYear(dateFuture.getFullYear() + 2);

      this.pageState.locationId = searchQuery.secondStatusId;
      this.locationId = searchQuery.secondStatusId;

      this.pageState.startDate = searchQuery.startDate;
      this.pageState.endDate = searchQuery.endDate;
      this.pageState.searchTerm = searchQuery.searchTerm;

      this.setReservationsPageState(this.pageState);

      // Start searching
      this.getChannelReservationStatusCount();
    },

    pageSelected(page) {
      //this.isSearchingCounter++;

      this.pageState.selectedStatus.page = page;
      this.setReservationsPageState(this.pageState);
      this.getChannelReservations();
    },

    goToReservation(reservation) {
      this.setReservation(reservation)

      this.$router.push({
        name: `reservations-list-detail`,
        params: { reservationKey: reservation.Key },
      })
    },

  },
}
</script>
